import { useCallback, useEffect } from 'react';
import { usePostHog } from '@posthog/react';
import { useUserActionMutation } from './useUserActionMutation';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { usePreviewMode } from '../contexts/PreviewModeContext';

type SuspiciousActivityEvent = 'tab_blur' | 'tab_focus' | 'paste' | 'fullscreen_exit';
type SuspiciousActivityContext = 'skill_challenge' | 'diagnostic' | 'exit_slip';

// One ActionType per event (rather than a single generic "SuspiciousActivity" type with the
// event name in `description`) so a report can filter/count by exact event type via a plain
// enum id join instead of parsing free text.
const SUSPICIOUS_ACTIVITY_ACTION_TYPE_IDS: Record<SuspiciousActivityEvent, string> = {
  tab_blur: 'ActionTypeNode:27',
  tab_focus: 'ActionTypeNode:28',
  paste: 'ActionTypeNode:29',
  fullscreen_exit: 'ActionTypeNode:30',
};

const SUSPICIOUS_ACTIVITY_DESCRIPTIONS: Record<SuspiciousActivityEvent, string> = {
  tab_blur: 'Tab Blur',
  tab_focus: 'Tab Focus',
  paste: 'Paste',
  fullscreen_exit: 'Fullscreen Exit',
};

// Flat, self-describing names rather than one event + a context property — matches the
// existing PostHog convention in this codebase (completed_exit_slip, navigated_to_isr,
// completed_skill_challenge), which never carries extra properties.
const POSTHOG_EVENT_NAMES: Record<SuspiciousActivityContext, Record<SuspiciousActivityEvent, string>> = {
  skill_challenge: {
    tab_blur: 'skill_challenge_blur',
    tab_focus: 'skill_challenge_focus',
    paste: 'skill_challenge_paste',
    fullscreen_exit: 'skill_challenge_fullscreen_exit',
  },
  diagnostic: {
    tab_blur: 'diagnostic_blur',
    tab_focus: 'diagnostic_focus',
    paste: 'diagnostic_paste',
    fullscreen_exit: 'diagnostic_fullscreen_exit',
  },
  exit_slip: {
    tab_blur: 'exit_slip_blur',
    tab_focus: 'exit_slip_focus',
    paste: 'exit_slip_paste',
    fullscreen_exit: 'exit_slip_fullscreen_exit',
  },
};

interface UseSuspiciousActivityTrackingOptions {
  skillId?: string;
  // Learnosity session id for a Skill Challenge attempt. Sent via
  // `learnositySessionSessionIdRaw`, which has no foreign key — the StudentSkillAssessment
  // row it would otherwise reference doesn't exist yet while the attempt is in progress (and
  // may never exist at all if the attempt is abandoned).
  assessmentSessionId?: string;
  // Learnosity session id for a Diagnostic attempt. Sent via the existing
  // `diagnosticSessionId` field — that column has never had a foreign key (see
  // useDiagnosticUserAction, which already sets it on a freshly-started, unsubmitted
  // diagnostic), so no "raw" workaround is needed here.
  diagnosticSessionId?: string;
  // Learnosity session id for an Exit Ticket attempt. Sent via
  // `learnosityExitSlipSessionIdRaw` for the same reason as `assessmentSessionId`: the
  // LearnosityExitSlipSession row the foreign key would point at is only created once the
  // exit ticket is submitted, and the id is generated client-side before Learnosity loads.
  exitSlipSessionId?: string;
}

// Tracks potentially suspicious activity (tab blur/focus, paste into an answer field) during
// a Learnosity-rendered assessment and persists each occurrence as a UserAction, so it can be
// surfaced to a teacher later rather than only living in the console. Shared between the
// Skill Challenge, Diagnostic and Exit Ticket flows — all three render into a
// `#learnosity_assess` container and are otherwise identical for this purpose, differing
// only in which session id field applies.
export const useSuspiciousActivityTracking = ({
  skillId,
  assessmentSessionId,
  diagnosticSessionId,
  exitSlipSessionId,
}: UseSuspiciousActivityTrackingOptions) => {
  const userRedux = useAppSelector(selectUser);
  const previewMode = usePreviewMode();
  const posthog = usePostHog();
  const [commitMutation] = useUserActionMutation();
  const context: SuspiciousActivityContext = assessmentSessionId
    ? 'skill_challenge'
    : exitSlipSessionId
    ? 'exit_slip'
    : 'diagnostic';

  const logSuspiciousActivity = useCallback(
    (event: SuspiciousActivityEvent) => {
      if (previewMode || !userRedux?.id) return;
      commitMutation({
        variables: {
          input: {
            userAction: {
              userId: `UserNode:${userRedux.id}`,
              actionTypeId: SUSPICIOUS_ACTIVITY_ACTION_TYPE_IDS[event],
              skillId,
              timeStamp: new Date().toISOString(),
              description: SUSPICIOUS_ACTIVITY_DESCRIPTIONS[event],
              learnositySessionSessionIdRaw: assessmentSessionId,
              diagnosticSessionId,
              learnosityExitSlipSessionIdRaw: exitSlipSessionId,
            },
          },
        },
      });
      posthog.capture(POSTHOG_EVENT_NAMES[context][event]);
    },
    [
      commitMutation,
      previewMode,
      userRedux?.id,
      skillId,
      assessmentSessionId,
      diagnosticSessionId,
      exitSlipSessionId,
      posthog,
      context,
    ],
  );

  useEffect(() => {
    if (previewMode) return;

    const handleVisibilityChange = () => {
      logSuspiciousActivity(document.hidden ? 'tab_blur' : 'tab_focus');
    };

    // Delegated on `document` rather than a ref into Learnosity's own DOM, filtered down to
    // elements inside #learnosity_assess — catches a paste into any Learnosity fill-in-the-
    // blank input regardless of that widget's internal markup.
    const handlePaste = (event: ClipboardEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest?.('#learnosity_assess')) return;
      logSuspiciousActivity('paste');
    };

    // Fires on both entering and leaving full screen; only the latter is suspicious.
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        logSuspiciousActivity('fullscreen_exit');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [logSuspiciousActivity, previewMode]);
};
